
import React from 'react';
import { Platform, Text, View } from 'react-native';
import { ViewStyle, TextStyle } from 'react-native';

// Import React Icons for web with better error handling
let ReactIcons: any = {};
if (Platform.OS === 'web') {
  try {
    // استخدام Dynamic Import للتحميل الآمن
    const Icons = require('react-icons/md');
    ReactIcons = {
      MdPerson: Icons.MdPerson,
      MdEmail: Icons.MdEmail,
      MdLock: Icons.MdLock,
      MdVisibility: Icons.MdVisibility,
      MdVisibilityOff: Icons.MdVisibilityOff,
      MdHome: Icons.MdHome,
      MdLogout: Icons.MdLogout,
      MdCheck: Icons.MdCheck,
      MdClose: Icons.MdClose,
      MdChevronLeft: Icons.MdChevronLeft,
      MdChevronRight: Icons.MdChevronRight,
      MdMenu: Icons.MdMenu,
      MdSearch: Icons.MdSearch,
      MdFavorite: Icons.MdFavorite,
      MdStar: Icons.MdStar,
      MdPhone: Icons.MdPhone,
      MdSettings: Icons.MdSettings,
      MdAdd: Icons.MdAdd,
      MdEdit: Icons.MdEdit,
      MdDelete: Icons.MdDelete,
      MdSave: Icons.MdSave,
      MdUpload: Icons.MdUpload,
      MdDownload: Icons.MdDownload,
      MdRefresh: Icons.MdRefresh,
      MdInfo: Icons.MdInfo,
      MdWarning: Icons.MdWarning,
      MdError: Icons.MdError,
      MdCheckCircle: Icons.MdCheckCircle,
    };
  } catch (error) {
    console.warn('React Icons not available, using emoji fallback');
    ReactIcons = {}; // التأكد من أن ReactIcons فارغ عند الفشل
  }
}

interface IconProps {
  name: string;
  size?: number;
  color?: string;
  style?: ViewStyle | TextStyle;
}

// Icon mapping for both platforms
const iconMapping: { [key: string]: { web: string; native: string } } = {
  'user': { web: 'MdPerson', native: '👤' },
  'email': { web: 'MdEmail', native: '✉️' },
  'lock': { web: 'MdLock', native: '🔒' },
  'eye': { web: 'MdVisibility', native: '👁️' },
  'eye-off': { web: 'MdVisibilityOff', native: '🙈' },
  'home': { web: 'MdHome', native: '🏠' },
  'logout': { web: 'MdLogout', native: '🚪' },
  'check': { web: 'MdCheck', native: '✓' },
  'close': { web: 'MdClose', native: '✕' },
  'chevron-left': { web: 'MdChevronLeft', native: '‹' },
  'chevron-right': { web: 'MdChevronRight', native: '›' },
  'menu': { web: 'MdMenu', native: '☰' },
  'search': { web: 'MdSearch', native: '🔍' },
  'heart': { web: 'MdFavorite', native: '♥' },
  'star': { web: 'MdStar', native: '★' },
  'phone': { web: 'MdPhone', native: '📞' },
  'settings': { web: 'MdSettings', native: '⚙️' },
  'add': { web: 'MdAdd', native: '+' },
  'edit': { web: 'MdEdit', native: '✎' },
  'delete': { web: 'MdDelete', native: '🗑️' },
  'save': { web: 'MdSave', native: '💾' },
  'upload': { web: 'MdUpload', native: '⬆️' },
  'download': { web: 'MdDownload', native: '⬇️' },
  'refresh': { web: 'MdRefresh', native: '🔄' },
  'info': { web: 'MdInfo', native: 'ℹ️' },
  'warning': { web: 'MdWarning', native: '⚠️' },
  'error': { web: 'MdError', native: '❌' },
  'success': { web: 'MdCheckCircle', native: '✅' },
};

export default function Icon({ name, size = 24, color = '#000', style }: IconProps) {
  const iconConfig = iconMapping[name];
  
  if (!iconConfig) {
    // Fallback for unknown icons
    if (__DEV__) {
      console.warn(`Icon "${name}" not found in iconMapping`);
    }
    return (
      <Text style={{ fontSize: size, color, textAlign: 'center', ...style }}>
        ?
      </Text>
    );
  }

  if (Platform.OS === 'web') {
    // Use React Icons on web
    const WebIconComponent = ReactIcons[iconConfig.web];
    
    if (WebIconComponent) {
      return (
        <View style={[{ width: size, height: size, justifyContent: 'center', alignItems: 'center' }, style]}>
          <WebIconComponent 
            size={size} 
            color={color}
            style={{ 
              display: 'block',
              lineHeight: 1,
              verticalAlign: 'middle'
            }}
          />
        </View>
      );
    } else if (__DEV__) {
      console.warn(`Web icon "${iconConfig.web}" not found, using emoji fallback`);
    }
  }

  // Fallback to emoji/unicode for native or when web icons fail
  const iconStyle: TextStyle = {
    fontSize: size,
    color: color,
    textAlign: 'center',
    lineHeight: size,
    width: size,
    height: size,
    ...style
  };

  return (
    <Text style={iconStyle}>
      {iconConfig.native}
    </Text>
  );
}
